import { Button, Grid, IconButton, Stack } from '@vapor/material'
import { useMemo } from 'react'
import { useState, useCallback, useEffect } from 'react'
import ContactInput, { Contact as SingleContact } from './contact-input'
import { v4 as uuid } from 'uuid'
import { faPlus } from '@fortawesome/free-solid-svg-icons'
import { faTrash } from '@fortawesome/pro-light-svg-icons'
import FormItem from '../form-fields/form-item/form-item'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

export type Contact<T> = SingleContact<T> & { key?: string }
type KeyContact<T> = SingleContact<T> & { key: string }

export type MultiContactInputError =
  | {
      message?: string
    }
  | {
      type?: { message?: string }
      contact?: { message?: string }
    }[]

export type MultiContactInputProps<T> = {
  getTypeId: (type: T) => string
  displayType: (type: T) => string
  value?: Contact<T>[]
  onChange?: (value: Contact<T>[]) => void
  types?: T[]
  columns?: '1' | '2'
  label?: string
  contactLabel?: string
  addLabel?: string
  error?: MultiContactInputError
  disabled?: boolean
  defaultType?: T
}

function MultiContactInput<T>(props: MultiContactInputProps<T>) {
  const {
    //
    getTypeId,
    displayType,
    value,
    onChange,
    types,
    error,
    disabled,
    label,
    contactLabel,
    addLabel,
    defaultType,
  } = props
  const [localValue, setLocalValue] = useState<KeyContact<T>[]>(value?.map((c) => getKeyContact(c)) || [])

  useEffect(() => {
    if (value && value.length > 0) {
      const newLocalValue = value.map((c) => getKeyContact(c))

      if (!areContactsEqual<T>(newLocalValue, localValue)) {
        setLocalValue(newLocalValue)
        onChange?.(newLocalValue)
      }
    } else {
      const emptyValue = [getKeyContact<T>({ contact: '' })]
      setLocalValue(emptyValue)
    }
  }, [value])

  const onLocalChange = useCallback(
    (newLocalValue: KeyContact<T>[]) => {
      if (!value && !areContactsEqual<T>(newLocalValue, localValue)) {
        setLocalValue(newLocalValue)
        onChange?.(newLocalValue)
      }
      if (value && !areContactsEqual<T>(newLocalValue, value)) {
        onChange?.(newLocalValue)
      }
    },
    [value, onChange, localValue],
  )

  const onContactChange = useCallback(
    (key: string, contact: SingleContact<T>) => {
      const newLocalValue = localValue.map((c) => {
        if (c.key !== key) {
          return c
        } else {
          return {
            ...contact,
            key: key,
          }
        }
      })
      onLocalChange(newLocalValue)
    },
    [localValue, onLocalChange],
  )

  const onDeleteClick = useCallback(
    (key: string) => {
      const newLocalValue = localValue.filter((c) => c.key !== key)
      onLocalChange(newLocalValue)
    },
    [localValue, onLocalChange],
  )
  const onAddClick = useCallback(() => {
    const newLocalValue = localValue.concat({
      contact: '',
      type: defaultType && types?.find((t) => getTypeId(t) === getTypeId(defaultType)),
      key: uuid(),
    })
    onLocalChange(newLocalValue)
  }, [localValue, onLocalChange, defaultType, types])

  const xs = useMemo(() => {
    if (props.columns === '2') {
      return 6
    }
    return 12
  }, [props.columns])

  const mainError = useMemo(() => {
    if (Array.isArray(error)) {
      return undefined
    }
    return error
  }, [error])

  const inputErrors = useMemo(() => {
    if (Array.isArray(error)) {
      return error
    }
    return undefined
  }, [error])

  return (
    <FormItem //
      fullWidth
      fieldLabel={label}
      error={mainError != undefined}
      errorMessage={mainError?.message}
    >
      <Grid container spacing={1}>
        {localValue.map((contact, index) => {
          return (
            <Grid item xs={xs} key={contact.key}>
              <Stack //
                direction="row"
                spacing={1}
              >
                <ContactInput //
                  getTypeId={getTypeId}
                  displayType={displayType}
                  value={contact}
                  types={types}
                  onChange={(value) => onContactChange(contact.key, value)}
                  error={inputErrors?.[index]}
                  disabled={disabled}
                  label={contactLabel}
                />
                <Button //
                  size="small"
                  sx={{ visibility: localValue.length > 1 && index < localValue.length ? 'visible' : 'hidden' }}
                  onClick={() => onDeleteClick(contact.key)}
                  disabled={disabled}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </Button>
              </Stack>
            </Grid>
          )
        })}
        <Grid item xs={12}>
          {addLabel ? (
            <Button //
              size="small"
              onClick={onAddClick}
              disabled={disabled}
              startIcon={<FontAwesomeIcon icon={faPlus} />}
            >
              {addLabel}
            </Button>
          ) : (
            <IconButton size="small" onClick={onAddClick} disabled={disabled}>
              <FontAwesomeIcon icon={faPlus} />
            </IconButton>
          )}
        </Grid>
      </Grid>
    </FormItem>
  )
}

export default MultiContactInput

function areContactsEqual<T>(a: Contact<T>[], b: Contact<T>[]) {
  if (a.length !== b.length) {
    return false
  }
  for (let i = 0; i < a.length; i++) {
    if (a[i].contact !== b[i].contact) {
      return false
    }
    if (a[i].type !== b[i].type) {
      return false
    }
  }
  return true
}

function getKeyContact<T>(value: Contact<T>): KeyContact<T> {
  return {
    ...value,
    key: value.key ?? uuid(),
  }
}
